import React, { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress, CssBaseline, Button, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Routes, Route, Navigate, useParams, Outlet, useSearchParams, useLocation } from 'react-router-dom';
import { User, Auth, isSignInWithEmailLink } from "firebase/auth";

import LoginPage from './components/LoginPage';
import PasswordLoginPage from './components/PasswordLoginPage';
import CreatePasswordPage from './components/CreatePasswordPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import AccessDeniedPage from './components/AccessDeniedPage';
import AuthActionHandler from './components/AuthActionHandler';
import MainLayout from './components/MainLayout';
import HockeyStandings from './components/HockeyStandings';
import CrossSeasonTrends from './components/CrossSeasonTrends';
import PlayerStats from './components/PlayerStats';
import LinksPage from './components/LinksPage';
import HomePage from './components/HomePage';
import RosterPage from './components/RosterPage';
import Puckle from './games/Puckle';
import TournamentPage from './components/TournamentPage';
import CalendarPage from './components/CalendarPage';
import VolunteerPage from './components/VolunteerPage';
import AdminPage from './components/AdminPage';
import SettingsPage from './components/SettingsPage';
import GameHighlightsListPage from './components/highlights/GameHighlightsListPage';
import GameWatchPage from './components/highlights/GameWatchPage';
import { SeasonProvider, useSeason, SeasonConfig } from './SeasonContext';
import { RosterProvider, useRoster } from './RosterContext';
import { SEASONS, CURRENT_SEASON } from './seasons';
import { useMultiAuth, MultiAuthUser, clearCachedAuthUser } from './hooks/useMultiAuth';

const theme = createTheme({
  typography: {
    fontFamily: '"Google Sans", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  palette: {
    background: {
      default: '#f5f5f5',
    },
  },
});

// Automatically scroll to the top of the viewport on route transitions
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// A wrapper component to handle the season key from the URL
const StandingsPageWrapper = () => {
    const { seasonKey } = useParams();
    const [searchParams] = useSearchParams();
    const { selectedSeason } = useSeason();
    
    let seasonConfig: SeasonConfig | undefined = SEASONS.find(s => s.key === seasonKey) as SeasonConfig | undefined;

    if (!seasonConfig && !seasonKey) {
        const seasonId = searchParams.get('seasonId');
        const divisionId = searchParams.get('divisionId');
        if (seasonId && divisionId) {
            seasonConfig = {
                key: 'custom',
                label: searchParams.get('title') || 'Custom Season',
                divisionName: searchParams.get('title') || 'Custom Division',
                chipLabel: searchParams.get('title') || 'Custom Season',
                seasonId,
                divisionId,
                totalTeams: parseInt(searchParams.get('totalTeams') || '0', 10),
                startSchedule: searchParams.get('startSchedule') || '',
                endSchedule: searchParams.get('endSchedule') || '',
                teamName: searchParams.get('teamName') || 'Chicago Jets',
                teamId: searchParams.get('teamId') || '',
                isCurrent: false,
            };
        } else {
            seasonConfig = selectedSeason;
        }
    }
    
    if (!seasonConfig) {
        seasonConfig = selectedSeason || (CURRENT_SEASON as SeasonConfig);
    }
    
    useEffect(() => {
        if (seasonConfig) {
            const labelParts = seasonConfig.label.split(' ');
            const seasonName = labelParts.length > 1 ? labelParts.slice(1).join(' ') : seasonConfig.label;
            document.title = `Jets '14 | ${seasonName}`;
        } else {
            document.title = "Jets '14 | Standings";
        }
    }, [seasonConfig]);

    if (!seasonConfig) return <div>Season not found</div>;
    return <HockeyStandings seasonConfig={seasonConfig} />;
};

// Application layout structure
interface AppLayoutProps {
  user: User | null;
  allUsers: MultiAuthUser[];
  onSwitchUser: (appId: string) => void;
  onAddAccount: () => void;
  onSignOut: () => void;
  onSignOutAll: () => void;
  isTeamManager: boolean;
}

const AppLayout = ({ 
  user, 
  allUsers, 
  onSwitchUser, 
  onAddAccount, 
  onSignOut, 
  onSignOutAll, 
  isTeamManager 
}: AppLayoutProps) => (
    <MainLayout 
      user={user} 
      allUsers={allUsers}
      onSwitchUser={onSwitchUser}
      onAddAccount={onAddAccount}
      onSignOut={onSignOut} 
      onSignOutAll={onSignOutAll}
      isTeamManager={isTeamManager}
    >
        <Outlet context={{ user, isTeamManager }} />
    </MainLayout>
);

function MainContent({ 
  user, 
  allUsers,
  onSwitchUser,
  onAddAccount,
  onSignOut,
  onSignOutAll 
}: { 
  user: User | null; 
  allUsers: MultiAuthUser[];
  onSwitchUser: (appId: string) => void;
  onAddAccount: () => void;
  onSignOut: () => void;
  onSignOutAll: () => void;
}) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthorized, isTeamManager, isAdmin, error, rosters, refetchRosters } = useRoster();

  const userEmailNorm = (user?.email || '').trim().toLowerCase();
  const isSuperAdmin = userEmailNorm === 'bwuest@gmail.com';
  const hasHighlightsAccess = isSuperAdmin || Boolean(isAdmin || isTeamManager);

  // Check for standalone/public access to standings
  if (location.pathname.startsWith('/standings') && (searchParams.get('standalone') === 'true' || searchParams.has('seasonId'))) {
    return (
      <Routes>
        <Route path="/standings/:seasonKey" element={<StandingsPageWrapper />} />
        <Route path="/standings" element={<StandingsPageWrapper />} />
      </Routes>
    );
  }

  // If user is explicitly denied by backend
  if (isAuthorized === false) {
    return <AccessDeniedPage userEmail={user?.email || 'Not available'} onSignOut={onSignOut} onRefreshAccess={refetchRosters} />;
  }

  // If connection failed due to network/server error and we have no cached roster data at all
  if (error && isAuthorized === null && (!rosters || Object.keys(rosters).length === 0)) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 3, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom color="error">
          Unable to connect to backend service
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button variant="outlined" onClick={() => refetchRosters()}>
          Retry Connection
        </Button>
      </Box>
    );
  }

  return (
    <Routes>
      <Route element={
        <AppLayout 
          user={user} 
          allUsers={allUsers}
          onSwitchUser={onSwitchUser}
          onAddAccount={onAddAccount}
          onSignOut={onSignOut} 
          onSignOutAll={onSignOutAll}
          isTeamManager={isTeamManager} 
        />
      }>
        <Route path="/" element={<HomePage />} />
        <Route path="/links" element={<LinksPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        {hasHighlightsAccess && (
          <>
            <Route path="/highlights" element={<GameHighlightsListPage />} />
            <Route path="/highlights/:gameId" element={<GameWatchPage />} />
            <Route path="/videos" element={<Navigate to="/highlights" replace />} />
            <Route path="/videos/:gameId" element={<GameWatchPage />} />
          </>
        )}
        <Route path="/volunteers" element={<VolunteerPage />} />
        <Route path="/volunteer" element={<Navigate to="/volunteers" replace />} />
        <Route path="/roster" element={<RosterPage />} />
        <Route path="/standings/:seasonKey" element={<StandingsPageWrapper />} />
        <Route path="/standings" element={<StandingsPageWrapper />} />
        <Route path="/tournaments/:tournamentKey" element={<TournamentPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/preferences" element={<Navigate to="/settings" replace />} />
        <Route path="/profile" element={<Navigate to="/settings" replace />} />
        
        {isAdmin && (
          <>
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/trends" element={<CrossSeasonTrends />} />
            <Route path="/player-stats" element={<PlayerStats />} />
          </>
        )}

        {/* Redirect any unmatched nested paths to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>

      {/* Full-screen games */}
      <Route path="/puckle" element={<Puckle />} />
    </Routes>
  );
}

function App() {
  const { 
    activeUser, 
    allUsers, 
    loading, 
    hasCachedAuth,
    cachedUser,
    switchActiveUser, 
    prepareNewAccountLogin, 
    signInWithEmailLinkMulti,
    logoutUser, 
    logoutAll,
    defaultAuth 
  } = useMultiAuth();

  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [newAuthInstance, setNewAuthInstance] = useState<Auth | null>(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const handleAddAccount = () => {
    const authInstance = prepareNewAccountLogin();
    setNewAuthInstance(authInstance);
    setIsAddingAccount(true);
  };

  const handleLoginSuccess = () => {
    setIsAddingAccount(false);
    setNewAuthInstance(null);
  };

  const placeholderUser = useMemo(() => {
    if (!hasCachedAuth || !cachedUser) return null;
    return {
      uid: cachedUser.uid,
      email: cachedUser.email,
      displayName: cachedUser.displayName,
      photoURL: cachedUser.photoURL,
      emailVerified: cachedUser.emailVerified ?? true,
      getIdToken: async () => '',
      isPlaceholder: true,
    } as unknown as User;
  }, [hasCachedAuth, cachedUser]);

  const effectiveUser = activeUser?.user || placeholderUser;

  // Handle Firebase Auth action codes (email verification, password reset, passwordless sign-in)
  if (location.pathname === '/auth/action' || isSignInWithEmailLink(defaultAuth, window.location.href)) {
    return (
      <Routes>
        <Route path="*" element={<AuthActionHandler onSignInWithEmailLink={signInWithEmailLinkMulti} />} />
      </Routes>
    );
  }

  // Check for standalone/public access to standings
  if (location.pathname.startsWith('/standings') && (searchParams.get('standalone') === 'true' || searchParams.has('seasonId'))) {
    return (
      <Routes>
        <Route path="/standings/:seasonKey" element={<StandingsPageWrapper />} />
        <Route path="/standings" element={<StandingsPageWrapper />} />
      </Routes>
    );
  }

  // Initial Firebase Auth check in progress with no cached credentials
  if (loading && !effectiveUser) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          bgcolor: 'background.default',
          gap: 2
        }}
      >
        <img 
          src={process.env.PUBLIC_URL + '/chicago_jets_shield_logo.jpg'} 
          alt="Chicago Jets Logo" 
          style={{ height: 64, width: 'auto', marginBottom: 8 }} 
        />
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Authenticating...
        </Typography>
      </Box>
    );
  }

  if (isAddingAccount) {
    const authToUse = newAuthInstance || defaultAuth;
    return (
      <Box sx={{ position: 'relative' }}>
        <Button 
          onClick={() => { setIsAddingAccount(false); setNewAuthInstance(null); }} 
          sx={{ position: 'absolute', top: 16, left: 16, zIndex: 10 }}
          variant="outlined"
        >
          ← Back to Team Portal
        </Button>
        <Routes>
          <Route path="/create-password" element={<CreatePasswordPage authInstance={authToUse} onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage authInstance={authToUse} onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/password-signin" element={<PasswordLoginPage authInstance={authToUse} onLoginSuccess={handleLoginSuccess} />} />
          <Route path="*" element={<LoginPage authInstance={authToUse} onLoginSuccess={handleLoginSuccess} />} />
        </Routes>
      </Box>
    );
  }

  if (!loading && !activeUser) {
    clearCachedAuthUser();
    return (
      <Routes>
        <Route path="/create-password" element={<CreatePasswordPage authInstance={defaultAuth} onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage authInstance={defaultAuth} onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/password-signin" element={<PasswordLoginPage authInstance={defaultAuth} onLoginSuccess={handleLoginSuccess} />} />
        <Route path="*" element={<LoginPage authInstance={defaultAuth} onLoginSuccess={handleLoginSuccess} />} />
      </Routes>
    );
  }

  if (activeUser && !activeUser.user.emailVerified) {
    return (
      <LoginPage 
        authInstance={activeUser.auth} 
        initialUnverifiedUser={activeUser.user}
        onLoginSuccess={handleLoginSuccess}
      />
    );
  }

  if (!effectiveUser) {
    return null;
  }

  return (
    <RosterProvider user={effectiveUser} isAuthReady={!loading && Boolean(activeUser)}>
      <ScrollToTop />
      <MainContent 
        user={effectiveUser} 
        allUsers={allUsers}
        onSwitchUser={switchActiveUser}
        onAddAccount={handleAddAccount}
        onSignOut={() => activeUser ? logoutUser(activeUser.appId) : {}}
        onSignOutAll={logoutAll}
      />
    </RosterProvider>
  );
}

function AppWrapper() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SeasonProvider>
        <App />
      </SeasonProvider>
    </ThemeProvider>
  );
}

export default AppWrapper;
